"use client";

import { motion, useInView } from "framer-motion";
import { type ReactNode, useRef } from "react";

// Fade in animation component
type FadeInProps = {
	children: ReactNode;
	className?: string;
	delay?: number;
	duration?: number;
	direction?: "up" | "down" | "left" | "right" | "none";
	distance?: number;
	once?: boolean;
};

export function FadeIn({
	children,
	className,
	delay = 0,
	duration = 0.6,
	direction = "up",
	distance = 30,
	once = true,
}: FadeInProps) {
	const ref = useRef<HTMLDivElement>(null);
	const isInView = useInView(ref, { once, margin: "-100px" });

	const directions = {
		up: { y: distance, x: 0 },
		down: { y: -distance, x: 0 },
		left: { x: distance, y: 0 },
		right: { x: -distance, y: 0 },
		none: { x: 0, y: 0 },
	};

	return (
		<motion.div
			ref={ref}
			initial={{ opacity: 0, ...directions[direction] }}
			animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
			transition={{
				duration,
				delay,
				ease: [0.25, 0.4, 0.25, 1],
			}}
			className={className}
		>
			{children}
		</motion.div>
	);
}

// Stagger container for animating children in sequence
type StaggerContainerProps = {
	children: ReactNode;
	className?: string;
	staggerDelay?: number;
	delayStart?: number;
};

export function StaggerContainer({
	children,
	className,
	staggerDelay = 0.1,
	delayStart = 0,
}: StaggerContainerProps) {
	const ref = useRef<HTMLDivElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });

	return (
		<motion.div
			ref={ref}
			initial="hidden"
			animate={isInView ? "visible" : "hidden"}
			variants={{
				hidden: {},
				visible: {
					transition: {
						staggerChildren: staggerDelay,
						delayChildren: delayStart,
					},
				},
			}}
			className={className}
		>
			{children}
		</motion.div>
	);
}

// Stagger item to be used inside StaggerContainer
type StaggerItemProps = {
	children: ReactNode;
	className?: string;
	direction?: "up" | "down" | "left" | "right" | "none";
	distance?: number;
};

export function StaggerItem({
	children,
	className,
	direction = "up",
	distance = 30,
}: StaggerItemProps) {
	const directions = {
		up: { y: distance, x: 0 },
		down: { y: -distance, x: 0 },
		left: { x: distance, y: 0 },
		right: { x: -distance, y: 0 },
		none: { x: 0, y: 0 },
	};

	return (
		<motion.div
			variants={{
				hidden: { opacity: 0, ...directions[direction] },
				visible: {
					opacity: 1,
					x: 0,
					y: 0,
					transition: {
						duration: 0.6,
						ease: [0.25, 0.4, 0.25, 1],
					},
				},
			}}
			className={className}
		>
			{children}
		</motion.div>
	);
}

// Animated text that reveals word by word
type AnimatedTextProps = {
	text: string;
	className?: string;
	delay?: number;
	wordDelay?: number;
	as?: "h1" | "h2" | "h3" | "h4" | "p" | "span";
};

export function AnimatedText({
	text,
	className,
	delay = 0,
	wordDelay = 0.08,
	as: Component = "p",
}: AnimatedTextProps) {
	const ref = useRef<HTMLDivElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });
	const words = text.split(" ");

	const MotionComponent = motion.create(Component);

	return (
		<MotionComponent ref={ref} className={className}>
			{words.map((word, i) => (
				<motion.span
					key={word}
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{
						duration: 0.5,
						delay: delay + i * wordDelay,
						ease: [0.25, 0.4, 0.25, 1],
					}}
					className="inline-block mr-[0.25em]"
				>
					{word}
				</motion.span>
			))}
		</MotionComponent>
	);
}

// Scale in animation
type ScaleInProps = {
	children: ReactNode;
	className?: string;
	delay?: number;
	duration?: number;
	once?: boolean;
};

export function ScaleIn({
	children,
	className,
	delay = 0,
	duration = 0.6,
	once = true,
}: ScaleInProps) {
	const ref = useRef<HTMLDivElement>(null);
	const isInView = useInView(ref, { once, margin: "-100px" });

	return (
		<motion.div
			ref={ref}
			initial={{ opacity: 0, scale: 0.9 }}
			animate={isInView ? { opacity: 1, scale: 1 } : {}}
			transition={{
				duration,
				delay,
				ease: [0.25, 0.4, 0.25, 1],
			}}
			className={className}
		>
			{children}
		</motion.div>
	);
}

// Counter animation for numbers
type CounterProps = {
	from?: number;
	to: number;
	duration?: number;
	className?: string;
	suffix?: string;
	prefix?: string;
};

export function Counter({
	from = 0,
	to,
	duration = 2,
	className,
	suffix = "",
	prefix = "",
}: CounterProps) {
	const ref = useRef<HTMLSpanElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });

	return (
		<motion.span
			ref={ref}
			className={className}
			initial={{ opacity: 0 }}
			animate={isInView ? { opacity: 1 } : {}}
		>
			{prefix}
			<motion.span
				initial={{ opacity: 1 }}
				animate={isInView ? { opacity: 1 } : {}}
			>
				{isInView ? (
					<CounterNumber from={from} to={to} duration={duration} />
				) : (
					from
				)}
			</motion.span>
			{suffix}
		</motion.span>
	);
}

function CounterNumber({
	from,
	to,
	duration,
}: {
	from: number;
	to: number;
	duration: number;
}) {
	return (
		<motion.span
			initial={{ opacity: 1 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.1 }}
		>
			<motion.span
				initial={{ display: "inline-block" }}
				animate={{ display: "inline-block" }}
			>
				<AnimatedNumber from={from} to={to} duration={duration} />
			</motion.span>
		</motion.span>
	);
}

function AnimatedNumber({
	from,
	to,
	duration,
}: {
	from: number;
	to: number;
	duration: number;
}) {
	const nodeRef = useRef<HTMLSpanElement>(null);

	return (
		<motion.span
			ref={nodeRef}
			initial={{ opacity: 1 }}
			animate={{ opacity: 1 }}
			onAnimationStart={() => {
				const node = nodeRef.current;
				if (!node) return;

				const startTime = performance.now();
				const animate = (currentTime: number) => {
					const elapsed = currentTime - startTime;
					const progress = Math.min(elapsed / (duration * 1000), 1);

					// Easing function
					const easeOut = 1 - (1 - progress) ** 3;
					const current = Math.floor(from + (to - from) * easeOut);

					node.textContent = current.toString();

					if (progress < 1) {
						requestAnimationFrame(animate);
					}
				};

				requestAnimationFrame(animate);
			}}
		>
			{from}
		</motion.span>
	);
}

// Floating element animation (for decorative elements)
type FloatingElementProps = {
	children: ReactNode;
	className?: string;
	duration?: number;
	distance?: number;
	delay?: number;
};

export function FloatingElement({
	children,
	className,
	duration = 6,
	distance = 20,
	delay = 0,
}: FloatingElementProps) {
	return (
		<motion.div
			className={className}
			animate={{
				y: [-distance / 2, distance / 2, -distance / 2],
			}}
			transition={{
				duration,
				repeat: Number.POSITIVE_INFINITY,
				ease: "easeInOut",
				delay,
			}}
		>
			{children}
		</motion.div>
	);
}

// Glow pulse animation wrapper
type GlowPulseProps = {
	children: ReactNode;
	className?: string;
};

export function GlowPulse({ children, className }: GlowPulseProps) {
	return (
		<motion.div
			className={className}
			animate={{
				boxShadow: [
					"0 0 20px rgba(201, 161, 90, 0.3), 0 0 40px rgba(201, 161, 90, 0.1)",
					"0 0 30px rgba(201, 161, 90, 0.5), 0 0 60px rgba(201, 161, 90, 0.2)",
					"0 0 20px rgba(201, 161, 90, 0.3), 0 0 40px rgba(201, 161, 90, 0.1)",
				],
			}}
			transition={{
				duration: 3,
				repeat: Number.POSITIVE_INFINITY,
				ease: "easeInOut",
			}}
		>
			{children}
		</motion.div>
	);
}
